import React, { useState } from "react";
import { ImageIcon, X, Paperclip, Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { resolveImage } from "../../../lib/cms";
import { Label } from "./cmsShared";
import MediaPicker from "./MediaPicker";
import RichText from "./RichText";

function ImageField({ value, onChange, readOnly, tid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-20 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-sapphire-deep/10 bg-parchment">
        {value ? <img src={resolveImage(value)} alt="" className="h-full w-full object-cover" /> : <ImageIcon size={22} className="text-stonegrey" />}
      </div>
      {!readOnly && (
        <div className="flex gap-2">
          <button type="button" onClick={() => setOpen(true)} className="btn-dark !px-4 !py-2 !text-xs" data-testid={`${tid}-choose`}>Choose</button>
          {value && <button type="button" onClick={() => onChange("")} className="font-sans text-xs text-garnet" data-testid={`${tid}-clear`}>Remove</button>}
        </div>
      )}
      {open && <MediaPicker kind="image" onClose={() => setOpen(false)} onPick={(p) => { onChange(p.ref); setOpen(false); }} />}
    </div>
  );
}

function AttachmentsField({ value = [], onChange, readOnly, tid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-2">
      {value.map((a, i) => (
        <div key={a.ref} className="flex items-center justify-between rounded-lg border border-sapphire-deep/10 bg-parchment/50 px-4 py-2.5" data-testid={`${tid}-item-${i}`}>
          <span className="flex items-center gap-2 font-sans text-sm text-sapphire-deep"><Paperclip size={14} /> {a.name}</span>
          {!readOnly && <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-garnet" aria-label="Remove"><X size={16} /></button>}
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={() => setOpen(true)} className="btn-dark !px-4 !py-2 !text-xs" data-testid={`${tid}-add`}>
          <Plus size={14} /> Attach file
        </button>
      )}
      {open && (
        <MediaPicker kind="any" onClose={() => setOpen(false)}
          onPick={(p) => { if (p.media) onChange([...value, { ref: p.ref, name: p.name }]); setOpen(false); }} />
      )}
    </div>
  );
}

export function Field({ f, value, onChange, readOnly, tid }) {
  const common = { value: value ?? "", disabled: readOnly, "data-testid": tid, className: "input-lodge disabled:bg-parchment/50" };
  let input;
  if (f.type === "textarea") input = <textarea rows={4} {...common} onChange={(e) => onChange(e.target.value)} className={`${common.className} resize-y`} />;
  else if (f.type === "select") input = (
    <select {...common} onChange={(e) => onChange(e.target.value)}>
      {f.options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
  else if (f.type === "image") input = <ImageField value={value} onChange={onChange} readOnly={readOnly} tid={tid} />;
  else if (f.type === "richtext") input = <RichText value={value} onChange={onChange} readOnly={readOnly} />;
  else if (f.type === "attachments") input = <AttachmentsField value={value || []} onChange={onChange} readOnly={readOnly} tid={tid} />;
  else input = <input type={f.type === "date" ? "date" : "text"} {...common} onChange={(e) => onChange(e.target.value)} />;
  return (
    <div className="block">
      <Label>{f.label}</Label>
      {input}
    </div>
  );
}

export function ItemsEditor({ spec, items = [], onChange, readOnly }) {
  const set = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  const blank = Object.fromEntries(spec.fields.map((f) => [f.k, f.type === "select" ? f.options[0] : ""]));
  return (
    <div className="space-y-5">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-sapphire-deep/10 bg-parchment/40 p-5" data-testid={`items-entry-${i}`}>
          <div className="mb-4 flex items-center justify-between">
            <p className="font-display text-lg font-semibold text-sapphire-deep">{spec.label} {i + 1}{it[spec.title] ? ` · ${it[spec.title]}` : ""}</p>
            {!readOnly && (
              <div className="flex gap-1 text-sapphire-deep/60">
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1.5 hover:bg-white disabled:opacity-30" data-testid={`items-up-${i}`}><ArrowUp size={15} /></button>
                <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="rounded p-1.5 hover:bg-white disabled:opacity-30" data-testid={`items-down-${i}`}><ArrowDown size={15} /></button>
                <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="rounded p-1.5 text-garnet hover:bg-white" data-testid={`items-remove-${i}`}><Trash2 size={15} /></button>
              </div>
            )}
          </div>
          <div className="grid gap-4">
            {spec.fields.map((f) => (
              <Field key={f.k} f={f} value={it[f.k]} onChange={(v) => set(i, f.k, v)} readOnly={readOnly} tid={`field-${i}-${f.k}`} />
            ))}
          </div>
        </div>
      ))}
      {!readOnly && (
        <button type="button" onClick={() => onChange([...items, blank])} className="btn-dark !px-5 !py-2.5" data-testid="items-add">
          <Plus size={15} /> Add {spec.label.toLowerCase()}
        </button>
      )}
    </div>
  );
}
